import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LogIn, Search, CheckCircle, XCircle, RefreshCw, Monitor, Globe } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LoginLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredLogs, setFilteredLogs] = useState([]);

  useEffect(() => {
    fetchLogs();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      setFilteredLogs(logs.filter(log => 
        log.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.ip_address?.includes(searchTerm)
      ));
    } else {
      setFilteredLogs(logs);
    }
  }, [logs, searchTerm]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/admin/login-logs?limit=200`, { withCredentials: true });
      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching login logs:', error);
      toast.error('Erro ao carregar logs de login');
    } finally {
      setLoading(false);
    }
  };

  const parseUserAgent = (ua) => {
    if (!ua) return { browser: 'Desconhecido', os: 'Desconhecido' };
    
    let browser = 'Outro';
    let os = 'Outro';
    
    if (ua.includes('Chrome')) browser = 'Chrome';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Safari')) browser = 'Safari';
    else if (ua.includes('Edge')) browser = 'Edge';
    
    if (ua.includes('Windows')) os = 'Windows';
    else if (ua.includes('Mac')) os = 'macOS';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iOS')) os = 'iOS';
    
    return { browser, os };
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Log de Logins
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Registo de todas as tentativas de autenticação no sistema
          </p>
        </div>
        <Button onClick={fetchLogs} variant="outline" disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          data-testid="search-login-logs"
          placeholder="Pesquisar por email, nome ou IP..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="pt-4 text-center">
            <div className="text-3xl font-bold text-[#0F172A]">{logs.length}</div>
            <p className="text-sm text-slate-600">Total de Logins</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-4 text-center">
            <div className="text-3xl font-bold text-emerald-600">
              {logs.filter(l => l.success).length}
            </div>
            <p className="text-sm text-slate-600">Bem Sucedidos</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-4 text-center">
            <div className="text-3xl font-bold text-red-600">
              {logs.filter(l => !l.success).length}
            </div>
            <p className="text-sm text-slate-600">Falhados</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-4 text-center">
            <div className="text-3xl font-bold text-blue-600">
              {new Set(logs.map(l => l.ip_address)).size}
            </div>
            <p className="text-sm text-slate-600">IPs Únicos</p>
          </CardContent>
        </Card>
      </div>

      {/* Logs Table */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LogIn className="w-5 h-5 text-slate-600" />
            Registos de Login ({filteredLogs.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-slate-400" />
              <p className="mt-2 text-slate-600">A carregar logs...</p>
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              Nenhum log de login encontrado
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-semibold text-slate-600">Estado</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-600">Utilizador</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-600">Email</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-600">IP</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-600">Dispositivo</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-600">Data/Hora</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLogs.map((log, index) => {
                    const { browser, os } = parseUserAgent(log.user_agent);
                    return (
                      <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4">
                          {log.success ? (
                            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Sucesso
                            </Badge>
                          ) : (
                            <Badge className="bg-red-50 text-red-700 border-red-200">
                              <XCircle className="w-3 h-3 mr-1" />
                              Falhou
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <span className="font-medium text-[#0F172A]">{log.name || '-'}</span>
                          {log.role && (
                            <Badge variant="outline" className="ml-2 text-xs capitalize">
                              {log.role}
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-4 text-slate-600">{log.email}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1 text-slate-600">
                            <Globe className="w-3 h-3" />
                            {log.ip_address}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1 text-slate-600 text-sm">
                            <Monitor className="w-3 h-3" />
                            {browser} / {os}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-slate-600 text-sm">
                          {log.timestamp ? new Date(log.timestamp).toLocaleString('pt-PT') : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginLogsPage;
